<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import { stopAllSounds } from '@ilteducation/audio-resources';
import type { Exercise } from '@ilteducation/exercise-service-types';
import { useCurriculumStore } from '@/store/curriculum';
import useAudioStore from '@/store/audio';
import useHasLibrary from '@/composables/use-has-library';
import { useLocale } from '@/plugins/translation';
import { useDisplay } from 'vuetify';
import { getCurriculumResourceId, TabInfo, Tabs, Video } from '@ilteducation/videos';
import { useCategories } from '@ilteducation/content-ref-data';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { useUserContext } from '@ilteducation/ilt-auth';
import config from '../../config/ConfigProvider';
import { useExerciseStore } from '@/store/exercises';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FilmTabsContent from '@/components/videos/FilmTabsContent.vue';
import { VSheet } from 'vuetify/components';
import useSimilarContentContext from '@/composables/use-similar-content-context';
import type { ArticleV1, BookV1 } from '@ilteducation/content-registry-types';
import { useMaterialsForVideoSearch } from '@/composables/use-material-for-video-search';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import useLanguageTranslations from '@/composables/use-language-translations';
import { TAB_KEYS, setVideoTabContext, trackTabAction } from '@/composables/use-video-tab-analytics';

const exerciseStore = useExerciseStore();
const { exercises } = storeToRefs(exerciseStore);
const curriculumStore = useCurriculumStore();

const hasLibrary = useHasLibrary();
const countryCode = config.value('countryCode');
const { setNarrationPlaying } = useAudioStore();
const { populateLanguage, languageTranslated } = useLanguageTranslations();

const props = defineProps<{
  video: Video;
  area: Area;
  subject: Category;
  areaVideos: Video[];
}>();

const enabledTabs = ref<{ [idx: number]: boolean }>({});

const activeTab = ref<number>(window.history.state?.videoTab ?? Tabs.keywords);
watch(activeTab, () => {
  stopAllSounds();
  setNarrationPlaying(false);
});

const { showCurriculum } = storeToRefs(curriculumStore);
const { t, l } = useLocale();
const { mobile, mdAndUp, smAndUp } = useDisplay();
const showTabContentDialog = ref(!mdAndUp.value && window.history.state?.videoTab !== undefined);

const validSubtitles = computed(() => props.video.subtitles.map(populateLanguage).filter(languageTranslated));
const getExercises = computed(() => exercises.value[props.video.resourceId] ?? []);
const quizIsActive = computed(() => !!props.video && !!getExercises.value.find((el: Exercise) => el.type === 'quiz'));
const route = useRoute();
const { categories } = useCategories();
const currentSubjectId = categories.value.find((cat) => cat.slug === route.params.subject)!.id;
const hasAudioAidSubscription = computed(() => {
  const userContext = useUserContext();
  return userContext.subscriptions.some((sub) => sub.type === 'dyslexia');
});

const switchTab = (idx: number) => {
  activeTab.value = idx;
};

const clickMobileTab = (idx: number) => {
  onTabOpened(idx);
  switchTab(idx);
  showTabContentDialog.value = true;
};

watch(
  enabledTabs,
  () => {
    const savedTab: Tabs | undefined = window.history.state?.videoTab;
    if (savedTab !== undefined && enabledTabs.value[savedTab]) {
      switchTab(savedTab);
      return;
    }
    const firstEnabledTab = Object.values(enabledTabs.value).findIndex((value) => value);
    if (firstEnabledTab !== -1) {
      switchTab(firstEnabledTab);
    }
  },
  { deep: true },
);

onBeforeRouteLeave((to) => {
  window.history.replaceState(
    { ...window.history.state, videoTab: to.name === 'GamesMatchThePairs' ? activeTab.value : undefined },
    '',
  );
});

const videoId = computed(() => props.video.id);
const subjectId = computed(() => props.subject.id);
const areaId = computed(() => props.area.id);
const contentIds = computed(() => [videoId.value]);
const contextKeyArticles = computed(() => `${videoId.value}-articles`);
const contextKeyBooks = computed(() => `${videoId.value}-books`);

watchEffect(() => {
  setVideoTabContext({ videoId: videoId.value, categoryId: subjectId.value, areaId: areaId.value });
});

function onTabOpened(idx: number) {
  trackTabAction(TAB_KEYS[idx as Tabs], 'tab_opened');
}

// Finishing a quiz offers a button that jumps to the textbooks tab, which is a real tab open.
const swapTab = (idx: number) => {
  onTabOpened(idx);
  switchTab(idx);
};

const { relatedContents: relatedArticles } = useSimilarContentContext<ArticleV1>(
  contextKeyArticles,
  contentIds,
  'ArticleV1',
);

const hasRelatedArticles = computed(() => relatedArticles?.value?.length > 0);

const { relatedContents } = useSimilarContentContext<BookV1>(contextKeyBooks, contentIds, 'BookPart');
const hasRelatedBooks = computed(() => relatedContents?.value?.length > 0);

const { result } = useMaterialsForVideoSearch(videoId, { categoryId: subjectId, areaId });
const hasMaterials = computed(() => result.value.length > 0);

watchEffect(() => {
  enabledTabs.value[Tabs.keywords] = props.video.keywords.length > 0;
  enabledTabs.value[Tabs.quiz] = quizIsActive.value || props.video.keywords.length > 2;
  enabledTabs.value[Tabs.immerse] = hasLibrary && hasAudioAidSubscription.value && hasRelatedBooks.value;
  enabledTabs.value[Tabs.subtitles] = validSubtitles.value.length > 0 || !!props.video.summary;
  // Exercises are merged into the quiz tab; keep the key present (set false) so the contiguous-key
  // ordering used by the first-enabled-tab watch stays intact.
  enabledTabs.value[Tabs.exercises] = false;
  enabledTabs.value[Tabs.curriculum] = showCurriculum.value;
  enabledTabs.value[Tabs.articles] = hasRelatedArticles.value && countryCode === 'se';
  enabledTabs.value[Tabs.materials] = config.value('showMaterials') && hasMaterials.value;
  enabledTabs.value[Tabs.videosInArea] = !mdAndUp.value;
});

const tabsInfo = computed<TabInfo[]>(() => [
  {
    title: t(l.film.filmsInArea),
    icon: 'fa-light fa-video',
    enabled: enabledTabs.value[Tabs.videosInArea],
    tooltip: false,
    value: Tabs.videosInArea,
  },
  {
    title: t(l.film.tabs.concept),
    icon: 'fa-light fa-square-list',
    enabled: enabledTabs.value[Tabs.keywords],
    tooltip: false,
    value: Tabs.keywords,
  },
  {
    title: quizIsActive.value ? t(l.film.tabs.quiz_and_exercises) : t(l.film.tabs.exercises),
    icon: 'fa-light fa-gamepad',
    enabled: enabledTabs.value[Tabs.quiz],
    tooltip: false,
    value: Tabs.quiz,
  },
  {
    title: t(l.film.tabs.subtitles),
    icon: 'fa-light fa-subtitles',
    enabled: enabledTabs.value[Tabs.subtitles],
    tooltip: false,
    value: Tabs.subtitles,
  },
  {
    title: t(l.film.tabs.materials),
    icon: 'fa-light fa-file-lines',
    enabled: enabledTabs.value[Tabs.materials],
    tooltip: false,
    value: Tabs.materials,
  },
  {
    title: t(l.film.tabs.articles),
    icon: 'fa-light fa-book-open',
    enabled: enabledTabs.value[Tabs.articles],
    tooltip: false,
    value: Tabs.articles,
  },
  {
    title: t(l.film.tabs.immerse),
    icon: 'fa-light fa-book',
    enabled: enabledTabs.value[Tabs.immerse],
    tooltip: true,
    tooltipText: t(l.film.immerseTooltip),
    value: Tabs.immerse,
  },
  {
    title: t(l.film.tabs.curriculum),
    icon: 'fa-light fa-graduation-cap',
    enabled: enabledTabs.value[Tabs.curriculum],
    tooltip: false,
    value: Tabs.curriculum,
  },
]);

const activeTabInfo = computed(() => {
  return tabsInfo.value.find((tab) => tab.value === activeTab.value);
});

watchEffect(async () => {
  await curriculumStore.loadCurriculumData(countryCode, getCurriculumResourceId(props.video, currentSubjectId));
});

const goBack = () => {
  showTabContentDialog.value = false;
};
</script>

<template>
  <v-container class="film-container bg-surface pb-0 px-0" :class="{ 'bg-surface-container-lowest': mdAndUp }">
    <v-row v-if="mdAndUp">
      <!-- Desktop view -->
      <div class="tabs-wrapper w-100 pl-sm-3 pl-0">
        <v-tabs
          v-model="activeTab"
          class="video-page-tabs ml-auto mr-auto w-100 px-16"
          color="secondary"
          show-arrows
          :hide-slider="true"
        >
          <template v-for="tab in tabsInfo.filter((t) => t.enabled)" :key="tab.value">
            <v-tooltip v-if="tab.tooltip" attach="main" location="top" :disabled="mobile" :aria-label="tab.tooltipText">
              <template #activator="{ props: tooltipProps }">
                <v-tab v-bind="tooltipProps" :value="tab.value" class="mr-1" @click="onTabOpened(tab.value!)">
                  <FontAwesomeIcon :icon="tab.icon" class="pr-2" />
                  {{ tab.title }}
                </v-tab>
              </template>
              {{ tab.tooltipText }}
            </v-tooltip>
            <v-tab v-else :value="tab.value" class="mr-1" @click="onTabOpened(tab.value!)">
              <FontAwesomeIcon :icon="tab.icon" class="pr-2" />
              <span>{{ tab.title }}</span>
            </v-tab>
          </template>
        </v-tabs>
      </div>
      <div class="tabs-content-frame bg-surface" style="min-height: 500px">
        <FilmTabsContent :active-tab="activeTab" :video="video" @swap-tab="swapTab" :area="area" :subject="subject" />
      </div>
    </v-row>
    <v-row v-else class="py-0 px-4" dense>
      <!-- Mobile/Tablet view -->
      <template v-for="tab in tabsInfo.filter((t) => t.enabled)" :key="tab.value">
        <v-col class="d-flex" :cols="smAndUp ? 4 : 6">
          <v-btn
            class="w-100 d-flex align-center justify-start px-3 video-tab-button"
            variant="outlined"
            :aria-label="tab.title"
            size="large"
            @click="clickMobileTab(tab.value!)"
          >
            <FontAwesomeIcon :icon="tab.icon" class="pr-2 flex-shrink-0" style="width: 24px" />
            <span class="mr-auto text-wrap mobile-tab-text">
              {{ tab.title }}
            </span>
            <FontAwesomeIcon icon="fa-solid fa-arrow-right" class="flex-shrink-0" />
          </v-btn>
        </v-col>
      </template>
    </v-row>
  </v-container>
  <!-- Mobile dialog that slides in from the right if a mobile Tab is clicked -->
  <v-dialog
    v-model="showTabContentDialog"
    fullscreen
    :scrim="false"
    persistent
    attach
    transition="slide-x-reverse-transition"
    :aria-label="activeTabInfo?.title"
  >
    <v-sheet
      ref="drawerContent"
      position="relative"
      color="surface"
      width="100vw"
      class="drawer-container d-flex flex-column gr-4"
    >
      <v-container class="pt-0">
        <FilmTabsContent
          :active-tab="activeTab"
          :video="video"
          :tabInfo="activeTabInfo"
          :show-header="true"
          :area="area"
          :subject="subject"
          :area-videos="areaVideos"
          @swap-tab="swapTab"
          @go-back="goBack"
        />
      </v-container>
    </v-sheet>
  </v-dialog>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
$border-color: rgba(var(--v-theme-gray), 0.3);

.film-container {
  max-width: none;
}

.custom-container {
  max-width: 93rem !important;
}

.video-page-tabs {
  max-width: 93rem !important;

  .v-tab {
    text-transform: none;
    letter-spacing: 0.1px;
    transition: all 0.3s ease;
    border-radius: 16px 16px 0 0;
  }
  .v-tab--selected {
    color: rgb(var(--v-theme-primary)) !important;
    background: rgb(var(--v-theme-surface)) !important;
    border-radius: 16px 16px 0 0;
    border-color: rgb(var(--v-theme-outline-variant));
    border-width: 1px 1px 0 1px;
    border-style: solid;
  }
}

.tabs-wrapper {
  box-shadow: inset 0 -1px 0 0 rgb(var(--v-theme-outline-variant));
}

.tabs-content-frame {
  width: 100%;
  overflow: hidden;
}

:deep(.v-btn__content) {
  width: 100%;
}

.video-tab-button {
  background-color: rgb(var(--v-theme-surface-bright)) !important;
}

.mobile-tab-text {
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;
  max-height: 48px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.drawer-container {
  overscroll-behavior: none;
  overflow-y: scroll;
  border-radius: 0;
}
</style>
